import React, { ReactElement, useCallback, useMemo } from "react";
import { QualityHubActionFormProps } from "./QualityHubActionForm.types";
import { FormSection, FormControl, FormTokenizedObjectInput } from "src/ui/components/Forms";
import { TextField, Checkbox } from "@fluentui/react";
import { localize } from "src/l10n";
import { SpintrTypes } from "src/typings";
import { Conditional } from "src/components/Conditional";
import SpintrDatePicker from "src/spintr/components/SpintrDatePicker";
import ITokenizedInputItem from "src/ui/components/Forms/ITokenizedInputItem";
import { queryUsersAsync } from "src/api";
import TinyEditorComponent from "src/ui/components/Tiny/TinyEditorComponent";

/**
 * QualityHubActionForm component
 *
 * @param {QualityHubActionFormProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubActionForm({
    canChangeContentStatus = false,
    canRequireVerification = false,
    listOwnerId,
    model,
    onModelChange,
}: QualityHubActionFormProps): ReactElement | null {
    const l10n = useMemo(() => ({
        description: localize("Beskrivning"),
        title: localize("Titel"),
    }), []);

    const onTitleChanged = useCallback(
        (_: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
            onModelChange((model) => ({
                ...model,
                title: newValue ?? "",
            }));
        }
        , [onModelChange]);

    const onRequireVerificationChanged = useCallback(
        (_: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
            onModelChange((model) => ({
                ...model,
                requireVerification: !!checked,
                verificationType: !checked
                    ? SpintrTypes.QualityHubVerificationType.None
                    : SpintrTypes.QualityHubVerificationType.Image,
            }));
        }, [onModelChange]);

    const onDescriptionChanged = useCallback(
        (newValue: string) => {
            onModelChange((model) => ({
                ...model,
                description: newValue ?? "",
            }));
        }
        , [onModelChange]);

    const onPublishedChanged = useCallback(
        (_: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
            onModelChange((model) => ({
                ...model,
                contentStatus: checked
                    ? SpintrTypes.ContentStatus.Published
                    : SpintrTypes.ContentStatus.Draft,
            }));
        }, [onModelChange]);

    const onDueByDateChanged = useCallback(
        (date: Date | null) => {
            onModelChange((model) => ({
                ...model,
                dueByDate: date,
            }));
        }, [onModelChange]);

    const onAutocompleteAssignee = useCallback(
        async (text: string): Promise<ITokenizedInputItem[]> => {
            if (!text || text.length <= 0) {
                return [];
            }

            const users = await queryUsersAsync({
                word: text,
                unitId: listOwnerId,
            });

            return users.map((user) => ({
                key: user.id,
                imageUrl: user.image,
                name: user.name,
                subText: user.roles?.map((r) => r.name).join(", ") || "",
                type: SpintrTypes.UberType.User,
            }));
        }, [listOwnerId]);

    const onAssigneesChanged = useCallback(
        (items: ITokenizedInputItem[]) => onModelChange((model) => ({
            ...model,
            assignees: items,
        })), []);

    return (
        <div className="QualityHubActionForm">
            <FormSection>
                <FormControl>
                    <TextField aria-required={true}
                               className="textField"
                               label={l10n.title}
                               name="title"
                               onChange={onTitleChanged}
                               required={true}
                               value={model.title} />
                </FormControl>
            </FormSection>

            <FormSection>
                <FormControl>
                    <TinyEditorComponent id="QualityHubActionFormDescription"
                                         label={l10n.description}
                                         content={model.description || ""}
                                         onChange={onDescriptionChanged}
                                         minHeight={160}
                                         isInModal />
                </FormControl>
            </FormSection>

            <FormSection>
                <FormControl>
                    <SpintrDatePicker ariaLabel={localize("Forfallodatum")}
                                      isClearable={true}
                                      isRequired={false}
                                      label={localize("Forfallodatum")}
                                      onChangeHandler={onDueByDateChanged}
                                      value={model.dueByDate} />
                </FormControl>
            </FormSection>

            <Conditional condition={!!listOwnerId}>
                <FormSection>
                    <FormControl label={localize("Delegera")}>
                        <FormTokenizedObjectInput autocomplete={onAutocompleteAssignee}
                                                  items={model.assignees}
                                                  onChange={onAssigneesChanged} />
                    </FormControl>
                </FormSection>
            </Conditional>

            <Conditional condition={canChangeContentStatus}>

                <FormSection title={localize("Status")}>
                    <Checkbox checked={model.contentStatus === SpintrTypes.ContentStatus.Published}
                              label={localize("Publicerad")}
                              name="contentStatus"
                              onChange={onPublishedChanged} />
                </FormSection>
            </Conditional>

            <Conditional condition={canRequireVerification}>

                <FormSection title={localize("QH_VERIFICATION")}>
                    <Checkbox checked={model.requireVerification}
                              label={localize("QH_REQUIRE_VERIFICATION")}
                              name="requireVerification"
                              onChange={onRequireVerificationChanged} />
                </FormSection>

            </Conditional>
        </div>
    );
}

export default QualityHubActionForm;

